import type { Panel, PanelRow, Section } from "@electro-planner/domain";

/**
 * Bygger ett enlinjeschema (SLD — single-line diagram) som ren SVG-sträng
 * från panelens rader och sektioner. Layouten är enkel:
 *
 *   - Titel + inmatning/huvudbrytare i sidhuvudet.
 *   - En rad per sektion med S-kod som rubrik.
 *   - Horisontell skena med säkringar/JFB/JFA/lastbrytare hängande under.
 *   - Om en sektion har fler rader än vad som får plats på skena-raden
 *     wrappar vi till en ny skena-rad inom samma sektion.
 *
 * SVG:n är måttsatt i mm och passar A4 stående (210 mm bred). Höjden är
 * dynamisk efter antal sektioner och rader — vid stora centraler kan
 * skenan bli längre än A4 vilket innebär att utskriften flyter över på
 * en extra sida. Det är acceptabelt för MVP (användaren kan också välja
 * "Skala till sida" i utskriftsdialogen).
 */

const PAGE_W = 210; // A4-bredd i mm
const MARGIN = 10;
const USABLE_W = PAGE_W - 2 * MARGIN;

const HEADER_H = 28;
const SECTION_HEADER_H = 7;
const BRANCH_W = 30; // 6 grenar per skena-rad vid A4 stående
const BRANCH_H = 54;

type Category = "DB" | "JFB" | "JFA" | "EB" | "LB" | "OTHER";

const CATEGORY_LABEL: Record<Category, string> = {
  DB: "MCB",
  JFB: "RCD",
  JFA: "RCBO",
  EB: "MCCB",
  LB: "SW",
  OTHER: "",
};

const CATEGORY_COLOR: Record<Category, string> = {
  DB: "#dbeafe",
  JFB: "#fef9c3",
  JFA: "#dcfce7",
  EB: "#fecaca",
  LB: "#e2e8f0",
  OTHER: "#f3f4f6",
};

export function buildPanelSld({
  panel,
  sections,
  rows,
}: {
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
}): string {
  // Gruppera rader per sektion; osektionerade sist.
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);
  const rowsBySection = new Map<string, PanelRow[]>();
  const unsectioned: PanelRow[] = [];
  for (const r of [...rows].sort((a, b) => a.order - b.order)) {
    if (r.sectionId) {
      const arr = rowsBySection.get(r.sectionId) ?? [];
      arr.push(r);
      rowsBySection.set(r.sectionId, arr);
    } else {
      unsectioned.push(r);
    }
  }

  type RenderSection = { code: string; label?: string; rows: PanelRow[] };
  const renderSections: RenderSection[] = [];
  for (const s of sortedSections) {
    const srows = rowsBySection.get(s.id) ?? [];
    if (srows.length > 0) {
      renderSections.push({ code: s.code, label: s.label, rows: srows });
    }
  }
  if (unsectioned.length > 0) {
    renderSections.push({ code: "—", label: "Osektionerade", rows: unsectioned });
  }

  const branchesPerRow = Math.max(1, Math.floor(USABLE_W / BRANCH_W));
  const parts: string[] = [];

  const title = `${panel?.designation ?? ""} ${panel?.title ?? "Central"}`.trim();
  const meta = [
    panel?.location ? `Plats: ${panel.location}` : "",
    panel?.enclosure ? `Kapsling: ${panel.enclosure}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  parts.push(renderHeader(title, meta, panel?.busbarAmps));

  let y = MARGIN + HEADER_H;

  for (const sec of renderSections) {
    const barRowCount = Math.ceil(sec.rows.length / branchesPerRow);

    parts.push(
      `<text x="${MARGIN}" y="${y + 4.5}" font-size="4" font-weight="700" fill="#0f172a">${escape(
        sec.code,
      )}${
        sec.label
          ? ` <tspan fill="#475569" font-weight="400">· ${escape(sec.label)}</tspan>`
          : ""
      }</text>`,
    );
    y += SECTION_HEADER_H;

    for (let br = 0; br < barRowCount; br++) {
      const slice = sec.rows.slice(br * branchesPerRow, (br + 1) * branchesPerRow);
      const barY = y + 3;
      const barX2 = MARGIN + slice.length * BRANCH_W;
      parts.push(
        `<line x1="${MARGIN}" y1="${barY}" x2="${barX2}" y2="${barY}" stroke="#0f172a" stroke-width="0.9" />`,
      );
      for (let i = 0; i < slice.length; i++) {
        const r = slice[i]!;
        const bx = MARGIN + i * BRANCH_W;
        parts.push(renderBranch(r, bx, barY));
      }
      y += BRANCH_H;
    }
  }

  if (renderSections.length === 0) {
    parts.push(
      `<text x="${PAGE_W / 2}" y="${MARGIN + HEADER_H + 20}" font-size="4" text-anchor="middle" fill="#94a3b8">Inga rader att visa</text>`,
    );
    y = MARGIN + HEADER_H + 40;
  }

  const totalH = Math.max(y + MARGIN, MARGIN + HEADER_H + 20);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}mm" height="${totalH}mm" viewBox="0 0 ${PAGE_W} ${totalH}" font-family="system-ui, -apple-system, 'Segoe UI', sans-serif"><rect width="${PAGE_W}" height="${totalH}" fill="white"/>${parts.join("")}</svg>`;
}

function renderHeader(title: string, meta: string, busbarAmps?: number): string {
  const supplyX = PAGE_W - MARGIN - 22;
  return `
    <text x="${MARGIN}" y="${MARGIN + 5}" font-size="5.5" font-weight="700" fill="#0f172a">${escape(title)}</text>
    <text x="${MARGIN}" y="${MARGIN + 10}" font-size="3" fill="#475569">${escape(meta)}</text>
    <text x="${supplyX}" y="${MARGIN + 5}" font-size="2.8" fill="#475569">Inmatning</text>
    <line x1="${supplyX + 6}" y1="${MARGIN + 6.5}" x2="${supplyX + 6}" y2="${MARGIN + 12}" stroke="#0f172a" stroke-width="0.6"/>
    <rect x="${supplyX + 2}" y="${MARGIN + 12}" width="8" height="6" fill="#f1f5f9" stroke="#0f172a" stroke-width="0.5" rx="0.6"/>
    <text x="${supplyX + 6}" y="${MARGIN + 16}" font-size="2.8" text-anchor="middle" fill="#0f172a">${busbarAmps ? `${busbarAmps}A` : "HB"}</text>
    <line x1="${supplyX + 6}" y1="${MARGIN + 18}" x2="${supplyX + 6}" y2="${MARGIN + 24}" stroke="#0f172a" stroke-width="0.6"/>
  `;
}

function renderBranch(r: PanelRow, x: number, barY: number): string {
  const centerX = x + BRANCH_W / 2;
  const cat = categorize(r);
  const fill = CATEGORY_COLOR[cat];

  const symY = barY + 2;
  const symH = 10;
  const afterSym = symY + symH;

  const specLabel = [
    r.ampRating !== undefined ? `${r.ampRating}A` : "",
    r.poles !== undefined ? `${r.poles}P` : "",
    r.characteristic ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const rcdLabel =
    r.sensitivity !== undefined
      ? `${r.sensitivity}mA${r.rcdType ? ` typ ${r.rcdType}` : ""}`
      : "";

  const cable = formatCable(r);
  const usage = r.usage ?? "";

  return `
    <g>
      <line x1="${centerX}" y1="${barY}" x2="${centerX}" y2="${symY}" stroke="#0f172a" stroke-width="0.4"/>
      <rect x="${centerX - 10}" y="${symY}" width="20" height="${symH}" fill="${fill}" stroke="#0f172a" stroke-width="0.35" rx="0.8"/>
      <text x="${centerX}" y="${symY + 4.2}" font-size="3" font-weight="700" text-anchor="middle" fill="#0f172a">${escape(r.designation)}</text>
      <text x="${centerX}" y="${symY + 8.2}" font-size="2.4" text-anchor="middle" fill="#334155">${escape(CATEGORY_LABEL[cat])}</text>
      <text x="${centerX}" y="${afterSym + 3.2}" font-size="2.4" text-anchor="middle" fill="#0f172a">${escape(specLabel)}</text>
      ${rcdLabel ? `<text x="${centerX}" y="${afterSym + 6.2}" font-size="2" text-anchor="middle" fill="#475569">${escape(rcdLabel)}</text>` : ""}
      <line x1="${centerX}" y1="${afterSym + 8}" x2="${centerX}" y2="${afterSym + 22}" stroke="#0f172a" stroke-width="0.35"/>
      ${cable ? `<text x="${centerX + 1}" y="${afterSym + 14}" font-size="2.2" fill="#1e293b">${escape(cable)}</text>` : ""}
      ${usage ? `<text x="${centerX}" y="${afterSym + 25}" font-size="2.4" font-weight="600" text-anchor="middle" fill="#0f172a">${escape(truncate(usage, 18))}</text>` : ""}
    </g>
  `;
}

function categorize(r: PanelRow): Category {
  const comp = (r.component ?? "").toUpperCase();
  if (comp.startsWith("JFA")) return "JFA";
  if (comp.startsWith("JFB")) return "JFB";
  if (comp.startsWith("DB")) return "DB";
  if (comp.startsWith("EB")) return "EB";
  if (comp.startsWith("LB")) return "LB";
  return "OTHER";
}

function formatCable(r: PanelRow): string {
  if (r.cableType && r.cableCores && r.cableArea !== undefined) {
    return `${r.cableType} ${r.cableCores}G${String(r.cableArea).replace(".", ",")}${
      r.cableLength ? ` · ${r.cableLength}m` : ""
    }`;
  }
  return r.cableType ?? "";
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
